import {AbstractStore} from "./abstract-store";
import {action, autorun, computed, makeObservable, observable} from "mobx";
import {Project} from "../models/project";
import {ApiLayerConfiguration} from "../config/auftragsverwaltung-api-configuration";
import {JsonSerializer, throwError} from "typescript-json-serializer";
import {Difference} from "../models/difference";
import {JournalStore} from "./journal-store-mobx";
import {PartialOrder, PartialOrderProps, PartialOrderType} from "../models/partial-order";

export interface PartialOrderStoreProps {
    layerConfig: ApiLayerConfiguration;
    journalStore: JournalStore;
    partialOrderType: PartialOrderType;
}

export class PartialOrderStore extends AbstractStore {

    partialOrderType: PartialOrderType;
    journalStore: JournalStore | undefined;

    /**
     * Loaded items (local store)
     */
    items = observable.map<string, PartialOrder>();

    constructor(props: PartialOrderStoreProps) {

        super({ layerConfig: props.layerConfig });

        this.partialOrderType = props.partialOrderType;
        this.journalStore = props.journalStore;

        makeObservable(this, {
            status: observable,
            dataStatus: observable,

            items: observable,
            selectedItem: observable,
            setSelectedItem: action,
            resetSelectedItem: action,
            searchTerm: observable,
            visibleItems: computed,

            addItem: action,
            removeItem: action,

            isDataLoading: computed,
            isDataLoaded: computed,
            isDataError: computed,
            isLoading: computed,
            isLoaded: computed,
            isNotFound: computed,
            isError: computed
        });
    }

    get visibleItems(): PartialOrder[] {
        // apply filters
        const filtered = [...this.items.values()].filter(() => {
            return true;
        });
        // sort
        // filtered.sort(PartialOrder.sortByName);
        return [ ...filtered];
    }

    loadItem(projectGuid: string): Promise<PartialOrder | null> {
        return new Promise((resolve, reject) => {
            this.setDataStatus("loading");

            // !! do not use loadItems here. Then it wouldn't be possible to sync the
            // observed properties without swapping the whole items!!
            this.queryFeatures(undefined, [projectGuid]).then((partialOrders: PartialOrder[]) => {
                if (partialOrders[0] && partialOrders[0].guid === projectGuid) {
                    const partialOrder = partialOrders[0];
                    const localPartialOrder = this.items.get(projectGuid);
                    if (localPartialOrder) {
                        // return partial order as long as merging process is running. partial order can be displayed with
                        // indicator that syncing is in process
                        // TODO: merge partial order with already loaded partial order
                    }
                    this.addItem(partialOrder);
                    this.setSelectedItem(partialOrder);
                    this.setDataStatus("loaded");

                    resolve(partialOrder);
                } else {
                    reject();
                    throw new Error("Partial order not found");
                }
            }).catch((error) => {
                console.error(error);
                this.setDataStatus("error");
                throw new Error("Error loading partial order");
            });
        });
    }

    /**
     * Request all partial orders from server and store in local store
     *
     * @param projectId
     * @param guids (Optional) IDs of partial orders
     */
    loadItems(projectId?: number, guids?: string[]): Promise<PartialOrder[]> {
        return new Promise((resolve, reject) => {
            this.setDataStatus("loading");

            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                this.queryFeatures(projectId, guids).then((partialOrders: PartialOrder[]) => {
                    if (partialOrders) {
                        // do only save to mobx when not requested with specified ids or contacts
                        const map = new Map<string, PartialOrder>();
                        partialOrders.forEach((es: PartialOrder) => {
                            map.set(es.guid, es);
                        });
                        this.addItem(map);
                        this.setDataStatus("loaded");

                        resolve(partialOrders);
                    } else {
                        this.setDataStatus("error");
                        reject();
                        throw new Error("Error requesting partial order.");
                    }
                });
            });
        });
    }

    /**
     * Create a new item with a feature
     * Store item to local store and select
     * @param projectId GUID of the related project
     * @param type
     * @param user
     */
    newItem(projectId: number, type: PartialOrderType,  user: string): Promise<PartialOrder | null> {
        return new Promise((resolve, reject) => {

            if (!projectId) {
                reject();
            }

            // generate random project id
            const randomIds = new Uint8Array(1);
            self.crypto.getRandomValues(randomIds);

            const partialOrder = new PartialOrder({
                // guid: crypto.randomUUID(),
                type,
                projectId,
                creator: user
            });

            // create feature
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                // TODO: find out why the feature template prototype does not return attributes
                partialOrder.feature = this.createFeature(null, partialOrder);

                // workaround: add missing prototype attributes
                PartialOrder.prototypeAttributes.forEach((attrName) => {
                    if (!partialOrder.feature.attributes[attrName]) {
                        partialOrder.feature.attributes[attrName] = null;
                    }
                });

                this.addItem(partialOrder);
                this.setSelectedItem(partialOrder);

                resolve(partialOrder);
            });
        });
    }

    /**
     * Save partial order to the server
     *
     * @param project
     * @param partialOrder
     * @param updated
     * @param user
     */
    save(project: Project,
         partialOrder: PartialOrder | null,
         updated: any,
         user: string): Promise<PartialOrder | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                if (!partialOrder) {
                    reject("Teilauftrag undefined when saving edits.");
                    return;
                }

                if (partialOrder.isNewRecord) {
                    partialOrder.feature = this.createFeature(null, partialOrder);

                    // log creation in journal
                    const idDiff = new Difference({p: "guid", n: partialOrder.guid});
                    this.journalStore?.journalizeItem(
                        project,
                        "partial-order",
                        "add",
                        user,
                        [idDiff],
                        "Neuer Teilauftrag"
                    );
                }

                if(!partialOrder.isNewRecord) {
                    // log changes to journal
                    let diffs: Difference[] = [];
                    diffs = this.compareAttributes(partialOrder, updated, partialOrder.comparableAttributesForType());

                    // do this before updating the attributes
                    this.journalStore?.journalizeItem(
                        project,
                        "partial-order",
                        "update",
                        user,
                        diffs,
                        "Teilauftrag geändert"
                    );
                }

                const feature = partialOrder?.feature;
                if (!feature) {
                    reject(`Feature undefined for partial order ${partialOrder.objectId}`);
                    return;
                }

                // Loop through updated attributes and assign
                // the updated values to feature attributes.
                if (updated) {
                    Object.keys(updated).forEach((name) => {
                        const dbFieldName = PartialOrder.databaseFieldNames.get(name);
                        if (!dbFieldName) {
                            reject();
                            throw new Error(`Database field not found for partial order ${name}`);
                        }
                        feature.attributes[dbFieldName] = updated[name];
                    });
                }

                const mode = partialOrder.isNewRecord ? "add" : "update";
                this.saveFeature(feature, mode, user).then((objectId: number) => {
                    const attrs = updated || {};
                    attrs["objectId"] = objectId;
                    Object.assign(partialOrder, attrs);
                    if (partialOrder.feature) {
                        // update feature attributes also to allow deleting feature without page reload
                        partialOrder.feature.attributes["OBJECTID"] = objectId;
                    }

                    resolve(partialOrder);
                }, (error) => {
                    console.error("Error saving partial order", error);
                    reject(error);
                });
            });
        });
    }

    /**
     * Delete an partial order
     *
     * @param project
     * @param partialOrder
     * @param user
     */
    delete(project: Project,
           partialOrder: PartialOrder | null,
           user: string): Promise<number | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                if (!project) {
                    reject("Project undefined when deleting partial order.");
                    return;
                }
                if (!partialOrder) {
                    reject("Partial order undefined when deleting.");
                    return;
                }

                if (partialOrder.isNewRecord) {
                    reject("This partial order is not saved. Does not have to be deleted.");
                    return;
                }

                // do not allow deleting when process is further than a specified step
                if (!project.isAlterable) {
                    reject("This project cannot be changed. Process is running.");
                    return;
                }

                const feature = partialOrder?.feature;
                if (!feature) {
                    reject(`Feature undefined for partial order ${partialOrder.guid}`);
                    return;
                }

                this.deleteFeature(feature).then((objectId: number) => {
                    this.resetSelectedItem();
                    this.removeItem(partialOrder);

                    // log deletion in journal
                    const valueDiff = new Difference({p: "riskEvaluationId", o: partialOrder.riskEvaluationId?.toString() || ""});
                    const idDiff = new Difference({p: "guid", o: partialOrder.guid});
                    this.journalStore?.journalizeItem(
                        project,
                        "partial-order",
                        "delete",
                        user,
                        [idDiff, valueDiff],
                        "Teilauftrag gelöscht"
                    );

                    resolve(objectId);
                });
            });
        });
    }

    queryFeatures(projectId?: number, partialOrderGuids?: string[]): Promise<PartialOrder[] | [] | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose();

                // Get a query object for the layer's current configuration
                // queryParams.outFields will be set to ["*"] to get values
                // for all available fields.
                const queryParams = this.layer.createQuery();

                if (projectId !== undefined) {
                    queryParams.where += ` AND PROJ_ID = ${projectId}`;
                }
                // load all features if no project ID is specified
                if (partialOrderGuids !== undefined) {
                    // Add to the layer's current definitionExpression
                    queryParams.where += ` AND GUID IN ('${partialOrderGuids.join("','")}')`;
                }

                // query the layer with the modified params object
                this.layer.queryFeatures(queryParams).then((results: __esri.FeatureSet) => {
                    if (results.features) {
                        const partialOrders = this.parseQueryResult(results);
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        resolve(partialOrders);
                    } else {
                        reject();
                    }
                }, (error) => {
                    throw new Error(`Error querying project + ${error}`);
                });
            });
        });
    }

    private parseQueryResult(data: __esri.FeatureSet): PartialOrder[] {

        const serializer = new JsonSerializer({
            errorCallback: throwError
        });

        const partialOrders: PartialOrder[] = [];
        data.features?.forEach((feature: any) => {
            const json = serializer.deserialize(feature.attributes, PartialOrder);
            const partialOrder = new PartialOrder(json as PartialOrderProps);
            partialOrder.feature = feature;

            // pass store type to partial order
            partialOrder.type = this.partialOrderType;

            partialOrders.push(partialOrder);
        });
        return partialOrders;
    }

}
